import { useCallback, useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Lock, SendHorizontal, X } from 'lucide-react';
import { AttachmentButton } from './AttachmentButton';
import { AttachmentPreview } from './AttachmentPreview';
import { DragDropOverlay } from './DragDropOverlay';
import { UploadProgress } from './UploadProgress';
import { Button } from '@/components/ui/button';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useLang } from '@/lib/i18n';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onSendPrivateMessage: (to: string, content: string) => void;
  onTyping: () => void;
  onStopTyping: () => void;
  disabled?: boolean;
  dmTarget?: string;
  onClearDm: () => void;
}

export function ChatInput({
  onSendMessage,
  onSendPrivateMessage,
  onTyping,
  onStopTyping,
  disabled,
  dmTarget,
  onClearDm,
}: ChatInputProps) {
  const { t } = useLang();
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const {
    attachments,
    addFiles,
    removeAttachment,
    uploadAttachments,
    isUploading,
    uploadProgress,
    uploadError,
    hasValidAttachments,
    dragActive,
    setDragActive,
  } = useFileUpload();

  const resetComposer = useCallback(() => {
    setMessage('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
    onStopTyping();
  }, [onStopTyping]);

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
    onTyping();
    if (typingTimeoutRef.current) window.clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = window.setTimeout(() => onStopTyping(), 1000);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleFileSelection = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    await addFiles(files);
  }, [addFiles]);

  const handleSend = useCallback(async () => {
    const trimmed = message.trim();
    if ((!trimmed && !hasValidAttachments) || disabled) return;

    if (attachments.length > 0) {
      const payloads = await uploadAttachments();
      if (payloads.length > 0) {
        payloads.forEach((payload) => {
          if (dmTarget) {
            onSendPrivateMessage(dmTarget, JSON.stringify(payload));
          } else {
            onSendMessage(JSON.stringify(payload));
          }
        });
      }
    }

    if (trimmed) {
      if (dmTarget) {
        onSendPrivateMessage(dmTarget, trimmed);
      } else {
        onSendMessage(trimmed);
      }
    }

    resetComposer();
  }, [attachments.length, dmTarget, disabled, hasValidAttachments, message, onSendMessage, onSendPrivateMessage, resetComposer, uploadAttachments]);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    await handleFileSelection(event.dataTransfer.files);
  }, [handleFileSelection, setDragActive]);

  const handlePaste = useCallback(async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardItems = Array.from(event.clipboardData?.items ?? []);
    const imageItem = clipboardItems.find((item) => item.kind === 'file' && item.type.startsWith('image/'));

    if (!imageItem) return;

    event.preventDefault();
    const file = imageItem.getAsFile();
    if (file) {
      await addFiles([file]);
    }
  }, [addFiles]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  return (
    <div className="shrink-0 relative z-20 bg-background/50 backdrop-blur-md border-t border-border sticky bottom-0 z-10">
      {dmTarget && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border-b border-primary/20 text-sm text-primary">
          <Lock className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1">
            {t('chat.privateLabel')} <strong>{dmTarget}</strong>
          </span>
          <button
            onClick={onClearDm}
            className="p-2 rounded-full hover:bg-primary/20 transition-colors text-primary/70 hover:text-primary touch-manipulation"
            aria-label={t('chat.cancelDm')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div
        className="relative p-3 md:p-4"
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(false);
        }}
        onDrop={handleDrop}
      >
        <DragDropOverlay visible={dragActive} />

        {uploadError && (
          <div className="mb-3 rounded-2xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {uploadError}
          </div>
        )}

        {attachments.length > 0 && (
          <div className="mb-3">
            <AttachmentPreview attachments={attachments} onRemove={removeAttachment} />
          </div>
        )}

        {isUploading && <UploadProgress isUploading={isUploading} progress={uploadProgress} />}

        <div className="flex items-end gap-2 md:gap-3 min-w-0">
          <AttachmentButton onClick={openFilePicker} disabled={disabled || isUploading} />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept="image/*,video/*,audio/*,.pdf,.zip,.txt,.doc,.docx,application/*"
            onChange={(event) => {
              void handleFileSelection(event.target.files);
              event.target.value = '';
            }}
          />
          <div className={`relative flex-1 min-w-0 bg-input rounded-2xl shadow-sm border transition-all overflow-hidden
            ${dmTarget
              ? 'border-primary/40 ring-1 ring-primary/20'
              : 'border-border/50 focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20'
            }`}>
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onBlur={() => onStopTyping()}
              onPaste={handlePaste}
              placeholder={dmTarget ? `${t('chat.privateLabel')} ${dmTarget}…` : t('chat.placeholder')}
              disabled={disabled || isUploading}
              className="w-full bg-transparent text-foreground placeholder:text-muted-foreground p-3 md:p-4 max-h-[120px] min-h-[52px] resize-none outline-none text-[15px] leading-relaxed block [scrollbar-width:none]"
              inputMode="text"
              enterKeyHint="send"
              rows={1}
            />
          </div>
          <Button
            onClick={() => {
              void handleSend();
            }}
            disabled={(!message.trim() && !hasValidAttachments) || disabled || isUploading}
            size="icon"
            className={`h-12 w-12 rounded-full flex-shrink-0 transition-all shadow-md disabled:opacity-50 touch-manipulation
              ${dmTarget
                ? 'bg-primary/80 hover:bg-primary text-primary-foreground'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
          >
            <SendHorizontal className="h-5 w-5" />
            <span className="sr-only">{t('chat.send')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
